import axios from 'axios';
import { refreshToken,logoutHandler,getToken } from './utils';

// Helper to check if refresh token exists
const hasRefreshToken = () => {
	return !!localStorage.getItem('refreshToken');
};

const byPassError = [400, 403, 404];
// CREATE AXIOS INSTANCE WITH COMMON CONFIG
const axiosInstance = axios.create({
	baseURL: '/api',
	headers: {
		'Content-Type': 'application/json',
	},
});

//AXIOS REQUEST INTERCEPTOR SETS THE TOKEN FOR REQUESTS
axiosInstance.interceptors.request.use((config) => {
	const token = getToken();

	// logoutHandler redirects via SPA navigation (no full page reload), so these
	// module-level flags survive a logout. Once a valid token exists again (the
	// user has logged back in), clear the stale logout state so requests aren't
	// permanently blocked with "Logout in progress".
	if (token && (isLoggingOut || refreshTokenInProgress)) {
		isLoggingOut = false;
		refreshTokenInProgress = false;
	}

	// If we're in the process of logging out, reject new requests immediately
	if (isLoggingOut) {
		return Promise.reject(new Error('Logout in progress'));
	}

	if (token) {
		config.headers['Authorization'] = `Bearer ${token}`;
	} else {
		// No token: don't send "Bearer null"/"Bearer undefined", which the
		// backend would try to verify and reject as "jwt malformed".
		delete config.headers['Authorization'];
	}
	return config;
});

//
let refreshTokenInProgress = false;
let axiosQueue = [];
let isLoggingOut = false; // Flag to prevent new requests during logout

const updateQueueTokens = (access_token) => {
	const callbacks = [...axiosQueue]; // Copy array before clearing
	axiosQueue = []; // Clear queue first to prevent re-entry
	callbacks.forEach(callback => {
		try {
			callback(access_token);
		} catch (error) {
			// Silently handle errors - they will be caught by the promise reject
			// Don't log here as it's expected when token refresh fails
		}
	});
};

const addSubscriber = (callback) => {
	axiosQueue.push(callback);
};

axiosInstance.interceptors.response.use((response) => {  
	return response;
}, async (error) => {
	const originalRequest = error.config;
	
	// Check if error has a response (network errors don't have response)
	if (!error.response) {
		return Promise.reject(error);
	}
	
	if (originalRequest.url !== '/auth/refreshToken' ) {   
		if (!byPassError.includes(error.response.status)) {
			if (error.response.status === 401 && !originalRequest._retry) {
				// If already logging out, reject immediately
				if (isLoggingOut) {
					return Promise.reject(error);
				}
				
				// Only attempt refresh if we have a refresh token
				if (!hasRefreshToken()) {
					// No refresh token available, logout immediately
					isLoggingOut = true;
					logoutHandler().catch(() => {
						// Ignore logout errors
					});
					return Promise.reject(error);
				}
				
				// Store the original error for use in the callback
				const originalError = error;
				// AXIOS RESPONSE MUST BE A PROMISE TO HANDLE MULTIPLE REQUESTS IN QUEUE
				let timeoutId = null;
				const retryOriginalRequest = new Promise((resolve, reject) => {
					let resolved = false;
					
					const cleanup = () => {
						if (timeoutId) {
							clearTimeout(timeoutId);
							timeoutId = null;
						}
					};
					
					addSubscriber(access_token => {
						if (resolved) return; // Prevent multiple resolutions
						
						try {
							resolved = true;
							cleanup();
							
							if (access_token) {
								originalRequest.headers['Authorization'] = 'Bearer ' + access_token;      
								resolve(axiosInstance(originalRequest));
							} else {
								// Create a proper error that preserves the original error's response
								const refreshError = originalError.response 
									? Object.assign(new Error('Session expired. Please sign in again.'), {
										response: originalError.response,
										config: originalError.config,
										isTokenRefreshError: true,
										code: 'SESSION_EXPIRED'
									})
									: new Error('Session expired. Please sign in again.');
								refreshError.isTokenRefreshError = true;
								refreshError.code = refreshError.code || 'SESSION_EXPIRED';
								reject(refreshError);
							}
						} catch (err) {
							// Ensure error is always caught
							if (!resolved) {
								resolved = true;
								cleanup();
								const error = new Error('Session expired. Please sign in again.');
								error.isTokenRefreshError = true;
								error.code = 'SESSION_EXPIRED';
								reject(error);
							}
						}
					});
					
					// Set a timeout to prevent requests from hanging indefinitely
					// If refresh takes too long, reject the request
					timeoutId = setTimeout(() => {
						if (!resolved) {
							resolved = true;
							const timeoutError = new Error('Token refresh timeout');
							timeoutError.isTokenRefreshError = true;
							reject(timeoutError);
						}
					}, 10000); // 10 second timeout
				});       
				if (!refreshTokenInProgress) {
					originalRequest._retry = true;
					refreshTokenInProgress = true;
					try {
						const access_token = await refreshToken();
						refreshTokenInProgress = false;
						updateQueueTokens(access_token);
					} catch (refreshError) {
						refreshTokenInProgress = false;
						
						// Set logout flag to prevent new requests
						isLoggingOut = true;
						
						// Check if error is due to missing session or invalid refresh token
						const errorMessage = typeof refreshError.response?.data === 'string' 
							? refreshError.response.data 
							: refreshError.response?.data?.message || '';
						const isSessionError = errorMessage === 'sesión no encontrada' || 
							errorMessage === 'session missing' ||
							refreshError.code === 'NO_REFRESH_TOKEN' ||
							refreshError.code === 'REFRESH_TOKEN_EXPIRED';
						
						// Clear localStorage immediately for session errors
						if (isSessionError) {
							localStorage.clear();
						}
						
						// Logout user immediately (don't await to prevent blocking)
						logoutHandler().catch(() => {
							// Ignore logout errors
						});
						
						// Signal failure to all queued requests after a brief delay
						// This gives logoutHandler time to redirect
						setTimeout(() => {
							updateQueueTokens(null);
						}, 100);
						
						// Return a user-friendly error
						const error = new Error('Session expired. Please sign in again.');
						error.isTokenRefreshError = true;
						error.code = 'SESSION_EXPIRED';
						return Promise.reject(error);
					}
				}     
				return retryOriginalRequest;      
			}
			// Only force a logout when an (already retried) request is still
			// UNAUTHORIZED. Server errors (5xx) and other non-auth statuses must
			// NOT log the user out — e.g. a 500 from sending an email should
			// surface as an error, not destroy the session.
			if (error.response.status === 401 && !isLoggingOut && (hasRefreshToken() || getToken())) {
				isLoggingOut = true;
				logoutHandler().catch(() => {
					// Ignore logout errors
				});
			}
			return Promise.reject(error);
		}
		return Promise.reject(error);

	} else {          
		// Refresh token endpoint failed
		if (error.response && (error.response.status === 401 || error.response.status === 500)) {
			// Check if it's a session missing error
			const errorMessage = typeof error.response.data === 'string' 
				? error.response.data 
				: error.response.data?.message || '';
			const isSessionError = errorMessage === 'sesión no encontrada' || 
				errorMessage === 'session missing' ||
				errorMessage === 'Unauthorized';
			
			// Set logout flag and clear localStorage immediately
			isLoggingOut = true;
			if (isSessionError) {
				localStorage.clear();
			}
			
			// Logout immediately - don't await to prevent blocking
			logoutHandler().catch(() => {
				// Ignore logout errors
			});
			
			const sessionError = new Error('Session expired. Please sign in again.');
			sessionError.isTokenRefreshError = true;
			sessionError.code = 'SESSION_EXPIRED';
			return Promise.reject(sessionError);
		}
		return Promise.reject(error);
	}
});
export default axiosInstance;
